import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronRight, Compass } from "lucide-react";
import { T } from "@/context/LanguageContext";

const IMG = {
  flag: "https://images.unsplash.com/photo-1626836014893-37663794dca7?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  map: "https://static.prod-images.emergentagent.com/jobs/aa01d845-57c4-4f49-beb6-589770c2c537/images/c0b39d1c14fa6719ed3d8964908f061bbb7a75ac25ff5b86137812acc8259f1f.jpeg",
};

// faint line-art globe (bottom-right accent, like the mockup)
function Globe({ className }) {
  return (
    <svg viewBox="0 0 200 200" className={className} fill="none" stroke="currentColor" strokeWidth="0.6" aria-hidden="true">
      <circle cx="100" cy="100" r="90" />
      <ellipse cx="100" cy="100" rx="40" ry="90" />
      <ellipse cx="100" cy="100" rx="70" ry="90" />
      <line x1="10" y1="100" x2="190" y2="100" />
      <path d="M18 62 H182 M18 138 H182" />
      <path d="M100 10 V190" />
    </svg>
  );
}

export function Hero() {
  const ref = useRef(null);

  return (
    <section ref={ref} className="relative overflow-hidden bg-parchment">
      {/* vintage map, full US visible on the right */}
      <div className="absolute inset-y-0 right-0 w-[68%]">
        <img src={IMG.map} alt="Vintage cartographic map of the United States" className="h-full w-full object-cover object-left" />
        <div className="absolute inset-0 bg-gradient-to-r from-parchment via-parchment/20 to-transparent" />
        <div className="absolute inset-y-0 left-0 w-1/3 bg-gradient-to-r from-parchment to-transparent" />
      </div>

      {/* flag left, fading into parchment */}
      <div className="absolute inset-y-0 left-0 w-[30%]">
        <img src={IMG.flag} alt="American flag" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-navy/15 via-parchment/50 to-parchment" />
        <div className="absolute inset-0 bg-gradient-to-t from-parchment/60 via-transparent to-parchment/20" />
      </div>

      {/* compass rose, top-right */}
      <motion.div
        className="pointer-events-none absolute right-[5%] top-[16%] hidden text-navy/25 lg:block"
        animate={{ rotate: 360 }}
        transition={{ repeat: Infinity, duration: 120, ease: "linear" }}
      >
        <Compass className="h-36 w-36" strokeWidth={0.5} />
      </motion.div>

      {/* faint globe, bottom-right */}
      <Globe className="pointer-events-none absolute -bottom-10 right-[8%] hidden h-56 w-56 text-navy/15 lg:block" />

      <div className="relative mx-auto max-w-[1500px] px-4">
        <div className="relative z-10 max-w-2xl rounded-2xl bg-parchment/50 p-6 py-16 backdrop-blur-[2px] md:bg-transparent md:p-0 md:py-28 md:backdrop-blur-0">
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-3 flex items-center gap-3"
          >
            <span className="h-[3px] w-12 bg-oldglory-red" />
            <span className="font-display text-xl font-medium text-navy md:text-3xl">
              <T>Explore the Pulse of</T>
            </span>
          </motion.div>

          <h1 className="font-display text-6xl font-black leading-[0.95] tracking-tight sm:text-7xl lg:text-8xl xl:text-[8.5rem]">
            <span className="block overflow-hidden pb-3">
              <motion.span
                initial={{ y: "110%" }}
                animate={{ y: "0%" }}
                transition={{ delay: 0.15, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                className="block"
              >
                <span className="text-navy">AME</span><span className="text-oldglory-red">RICA</span>
              </motion.span>
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.55, duration: 0.7 }}
            className="mt-5 max-w-md text-base text-ink/80 md:text-lg"
          >
            <T>Discover the rich history, diverse culture, people, places and opportunities across the United States.</T>
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.7 }}
            className="mt-8 flex flex-wrap gap-4"
          >
            <Link to="/explore/states" className="group inline-flex items-center gap-2 rounded-md bg-oldglory-red px-7 py-3.5 font-semibold text-white shadow-lg shadow-oldglory-red/25 transition-all hover:bg-oldglory-dark hover:shadow-xl" data-testid="hero-explore-states">
              <T>Explore States</T>
              <ChevronRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
            <Link to="/atlas" className="group inline-flex items-center gap-2 rounded-md border-2 border-navy bg-white/70 px-7 py-3.5 font-semibold text-navy transition-all hover:bg-navy hover:text-white" data-testid="hero-learn-more">
              <T>Open the Atlas</T>
              <ChevronRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
